import { Badge, Box, Button, Drawer, Icon, Theme } from "@outschool/backpack";
import {
  SectionMessagesSummaryQuery,
  SectionMessagesSummaryQueryVariables,
  ThreadsForSectionQuery,
  ThreadsForSectionQueryVariables,
} from "@outschool/gql-frontend-generated";
import { faComment } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import {
  useApolloClient,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { UserHeadshotImage } from "@outschool/ui-components-shared";
import { useIsLarge, useIsMobile } from "@outschool/ui-utils";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useCurrentLearner } from "../../providers/CurrentLearnerProvider";
import {
  PostType,
  useMessageTeacher,
} from "../../providers/MessageTeacherProvider";
import { useLearnerClassroomAssignmentsBySectionQuery } from "../../queries/LearnerClassroomAssignmentsBySectionQueries";
import {
  SECTION_MESSAGES_SUMMARY_QUERY,
  THREADS_FOR_SECTION_QUERY,
} from "../../queries/ThreadsForSectionQuery";
import AssignmentPublicThreadDetailView from "./AssignmentPublicThreadDetailView";
import { MESSAGE_TEACHER_POLL_INTERVAL_MS, VIEW } from "./constants";
import FeedbackThreadDetailView from "./FeedbackThreadDetailView";
import { MessageTeacherDrawerProvider } from "./MessageTeacherDrawerContext";
import NewThreadView from "./NewThreadView";
import PublicThreadDetailView from "./PublicThreadDetailView";
import ThreadDetailView from "./ThreadDetailView";
import ThreadListView from "./ThreadListView";
import {
  ConversationListItem,
  DrawerView,
  FeedbackThreadItem,
  PostTarget,
  PublicThreadItem,
} from "./types";
import { useMarkCommentThreadRead } from "./useMarkCommentThreadRead";
import {
  getConversationDetailView,
  getThreadPostTitle,
  isGeneralThread,
  isSamePublicThreadTarget,
  toAssignmentPublicThreadItem,
  toBubbleMessageFromAssignmentComment,
  toPublicThreadItem,
} from "./utils";

interface MessageTeacherButtonProps {
  sectionUid: string;
  useTabletCompactLayout?: boolean;
}

const THREAD_UID_PARAM = "threadUid";
const MESSAGE_UID_PARAM = "messageUid";

function getMessageTeacherIntent(searchParams: URLSearchParams) {
  const threadUid = searchParams.get(THREAD_UID_PARAM);
  const messageUid = searchParams.get(MESSAGE_UID_PARAM);

  if (!threadUid && !messageUid) {
    return null;
  }

  return {
    threadUid,
    messageUid,
  };
}

function getInitialIntent() {
  if (typeof window === "undefined") {
    return null;
  }
  return getMessageTeacherIntent(new URLSearchParams(window.location.search));
}

function consumeMessageTeacherIntentQueryParams() {
  if (typeof window === "undefined") {
    return;
  }

  const url = new URL(window.location.href);
  url.searchParams.delete(THREAD_UID_PARAM);
  url.searchParams.delete(MESSAGE_UID_PARAM);

  const nextSearch = url.searchParams.toString();
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${
    url.hash
  }`;
  window.history.replaceState(window.history.state, "", nextUrl);
}

export default function MessageTeacherButton({
  sectionUid,
  useTabletCompactLayout = false,
}: MessageTeacherButtonProps) {
  const { t } = useTranslation("learnerApp\\MessageTeacherButton");
  const isLarge = useIsLarge();
  const isMobile = useIsMobile();
  const isCompact = isMobile || (useTabletCompactLayout && !isLarge);
  const currentLearner = useCurrentLearner();
  const { registerOpenForPost } = useMessageTeacher();
  const learnerInfo = {
    uid: currentLearner!.uid,
    name: currentLearner?.name ?? "",
    avatar: currentLearner?.avatar ?? null,
  };
  const [initialIntent] = useState(getInitialIntent);
  const [open, setOpen] = useState(() => initialIntent !== null);
  const [view, setView] = useState<DrawerView>(() => {
    if (initialIntent?.threadUid) {
      return {
        kind: VIEW.THREAD,
        threadUid: initialIntent.threadUid,
        postTitleOverride: null,
      };
    }
    return { kind: VIEW.LIST };
  });
  const [highlightMessageUid, setHighlightMessageUid] = useState<string | null>(
    () => initialIntent?.messageUid ?? null,
  );
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // The trigger badge is visible on every classroom page, so only this
  // lightweight query polls while the drawer is closed.
  const { data: summaryData } = useQueryWithPreviousData<
    SectionMessagesSummaryQuery,
    SectionMessagesSummaryQueryVariables
  >(SECTION_MESSAGES_SUMMARY_QUERY, {
    variables: { sectionUid },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
  });

  const {
    data,
    loading: threadsLoading,
    refetch,
  } = useQueryWithPreviousData<
    ThreadsForSectionQuery,
    ThreadsForSectionQueryVariables
  >(THREADS_FOR_SECTION_QUERY, {
    variables: {
      sectionUid,
      searchQuery: debouncedSearch || undefined,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: MESSAGE_TEACHER_POLL_INTERVAL_MS,
    skip: !open,
  });

  const unreadCount =
    (summaryData?.section?.unreadThreadsCount ??
      data?.section?.unreadThreadsCount ??
      0) + (summaryData?.section?.learnerUnreadCommentThreadsCount ?? 0);
  const leader = data?.section?.leader ?? summaryData?.section?.leader;
  const threads = useMemo(
    () => data?.section?.threads ?? [],
    [data?.section?.threads],
  );
  const publicThreads = useMemo<PublicThreadItem[]>(
    () => [
      ...(data?.section?.learnerPublicCommentThreads ?? []).map(
        toPublicThreadItem,
      ),
      ...(data?.section?.learnerAssignmentPublicCommentThreads ?? []).map(
        toAssignmentPublicThreadItem,
      ),
    ],
    [
      data?.section?.learnerPublicCommentThreads,
      data?.section?.learnerAssignmentPublicCommentThreads,
    ],
  );
  const { data: assignmentData, loading: feedbackThreadsLoading } =
    useLearnerClassroomAssignmentsBySectionQuery(
      { sectionUid },
      { skip: !open },
    );
  const sectionEndTime = data?.section?.end_time ?? null;
  const unreadFeedbackAssignmentUids =
    data?.section?.learnerUnreadFeedbackAssignmentClassPostUids;
  const feedbackThreads = useMemo<FeedbackThreadItem[]>(() => {
    const assignments = assignmentData?.section?.assignmentClassPosts ?? [];
    const lowerSearch = debouncedSearch.trim().toLowerCase();
    const unreadAssignmentUids = new Set(unreadFeedbackAssignmentUids ?? []);

    return assignments.flatMap(assignment => {
      const submission = assignment.comments.find(
        comment =>
          comment.isSubmission === true &&
          comment.sender.uid === learnerInfo.uid,
      );
      if (!submission) {
        return [];
      }

      const messageNodes = [submission, ...(submission.replies ?? [])];
      const messages = messageNodes
        // @ts-ignore TS7
        .map(toBubbleMessageFromAssignmentComment)
        .sort((a, b) => a.sentAt.localeCompare(b.sentAt));

      if (messages.length === 0) {
        return [];
      }

      const assignmentTitle =
        assignment.title?.trim() || t("Untitled assignment");
      if (lowerSearch) {
        const titleMatches = assignmentTitle
          .toLowerCase()
          .includes(lowerSearch);
        const contentMatches = messages.some(message =>
          message.messageContent.text?.toLowerCase().includes(lowerSearch),
        );
        if (!titleMatches && !contentMatches) {
          return [];
        }
      }

      return [
        {
          kind: "feedback" as const,
          uid: `feedback-${assignment.uid}-${submission.uid}`,
          assignmentClassPostUid: assignment.uid,
          assignmentTitle,
          latestMessage: messages[messages.length - 1] ?? null,
          messages,
          unreadCount: unreadAssignmentUids.has(assignment.uid) ? 1 : 0,
        },
      ];
    });
  }, [
    assignmentData?.section?.assignmentClassPosts,
    debouncedSearch,
    learnerInfo.uid,
    t,
    unreadFeedbackAssignmentUids,
  ]);

  const apolloClient = useApolloClient();
  const openForPost = useCallback(
    async (postUid: string, postType: PostType, postTitle: string) => {
      setOpen(true);
      // The drawer query is skipped while closed, so fetch the unfiltered
      // thread list here rather than reading a list that may not be loaded.
      let threadsData: ThreadsForSectionQuery | undefined;
      try {
        ({ data: threadsData } = await apolloClient.query<
          ThreadsForSectionQuery,
          ThreadsForSectionQueryVariables
        >({
          query: THREADS_FOR_SECTION_QUERY,
          variables: { sectionUid, searchQuery: undefined },
          fetchPolicy: "network-only",
        }));
      } catch {
        // Leave the drawer on the conversation list, which retries the query.
        return;
      }
      const match = (threadsData?.section?.threads ?? []).find(thread => {
        if (postType === "assignmentPost") {
          return thread.learningAssignmentClassPost?.uid === postUid;
        }
        return thread.classPost?.uid === postUid;
      });

      if (match) {
        setView({
          kind: VIEW.THREAD,
          threadUid: match.uid,
          postTitleOverride: null,
        });
      } else {
        const postTarget: PostTarget =
          postType === "classPost"
            ? { classPostUid: postUid }
            : { learningAssignmentClassPostUid: postUid };
        setView({
          kind: VIEW.NEW_THREAD,
          pendingPost: { kind: "post", postTarget, postTitle },
        });
      }
    },
    [apolloClient, sectionUid],
  );

  useEffect(() => {
    registerOpenForPost(openForPost);
  }, [registerOpenForPost, openForPost]);

  const handleClose = () => {
    consumeMessageTeacherIntentQueryParams();
    setOpen(false);
    setView({ kind: VIEW.LIST });
    setSearchInput("");
    setDebouncedSearch("");
    setHighlightMessageUid(null);
  };

  const handleThreadCreated = (threadUid: string) => {
    const postTitleOverride =
      view.kind === VIEW.NEW_THREAD && view.pendingPost.kind === "post"
        ? view.pendingPost.postTitle
        : null;
    setView({ kind: VIEW.THREAD, threadUid, postTitleOverride });
    refetch();
  };

  const selectedThread =
    view.kind === VIEW.THREAD
      ? threads.find(t => t.uid === view.threadUid)
      : null;
  const selectedFeedbackThread =
    view.kind === VIEW.FEEDBACK
      ? feedbackThreads.find(thread => thread.uid === view.feedbackThreadUid) ??
        null
      : null;
  const selectedPublicThread =
    view.kind === VIEW.PUBLIC
      ? publicThreads.find(thread =>
          isSamePublicThreadTarget(thread.target, view.target),
        ) ?? null
      : null;
  useMarkCommentThreadRead(
    sectionUid,
    selectedFeedbackThread ?? selectedPublicThread,
  );
  const teacherName = leader?.name?.trim() || t("Teacher");

  const sectionInfo = { uid: sectionUid, end_time: sectionEndTime };

  const handleOpenGeneralThread = () => {
    const existingGeneral = threads.find(isGeneralThread);
    if (existingGeneral) {
      setView({
        kind: VIEW.THREAD,
        threadUid: existingGeneral.uid,
        postTitleOverride: null,
      });
    } else {
      setView({ kind: VIEW.NEW_THREAD, pendingPost: { kind: "general" } });
    }
  };

  const drawerContextValue = {
    section: sectionInfo,
    learner: learnerInfo,
    leader: leader ?? null,
    teacherName,
    threads,
    feedbackThreads,
    publicThreads,
    threadsLoading,
    feedbackThreadsLoading,
    searchInput,
    debouncedSearch,
    setSearchInput,
    highlightMessageUid,
  };

  const handleConversationClick = useCallback((item: ConversationListItem) => {
    setView(getConversationDetailView(item));
  }, []);

  return (
    <>
      {isCompact ? (
        <Badge badgeContent={unreadCount} color="error">
          <Button
            variant="contained"
            onClick={() => setOpen(true)}
            aria-label={t("All Messages")}
            sx={{
              minWidth: 0,
              width: 48,
              height: 48,
              borderRadius: "50%",
              padding: 0,
            }}
          >
            <Icon icon={faComment} sx={{ fontSize: "1.5rem" }} />
          </Button>
        </Badge>
      ) : (
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: 48,
            whiteSpace: "nowrap",
          }}
        >
          <Badge badgeContent={unreadCount} color="error" sx={{ mr: 8 }}>
            <UserHeadshotImage user={leader} size={24} />
          </Badge>
          <Box component="span">{t("All Messages")}</Box>
        </Button>
      )}
      <Drawer
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: (theme: Theme) => ({
            width: 400,
            maxWidth: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            [theme.breakpoints.down("sm")]: {
              width: "85%",
            },
          }),
        }}
      >
        <MessageTeacherDrawerProvider value={drawerContextValue}>
          {view.kind === VIEW.THREAD ? (
            <ThreadDetailView
              threadUid={view.threadUid}
              postTitle={
                getThreadPostTitle(selectedThread) ?? view.postTitleOverride
              }
              onBack={() => {
                setHighlightMessageUid(null);
                setView({ kind: VIEW.LIST });
              }}
              onClose={handleClose}
            />
          ) : view.kind === VIEW.FEEDBACK ? (
            selectedFeedbackThread ? (
              <FeedbackThreadDetailView
                feedbackThread={selectedFeedbackThread}
                onBack={() => {
                  setHighlightMessageUid(null);
                  setView({ kind: VIEW.LIST });
                }}
                onClose={handleClose}
              />
            ) : (
              <ThreadListView
                onConversationClick={handleConversationClick}
                onOpenGeneralThread={handleOpenGeneralThread}
                onClose={handleClose}
              />
            )
          ) : view.kind === VIEW.PUBLIC ? (
            view.target.kind === "assignment" ? (
              <AssignmentPublicThreadDetailView
                assignmentClassPostUid={view.target.assignmentClassPostUid}
                postTitle={view.postTitle}
                onBack={() => setView({ kind: VIEW.LIST })}
                onClose={handleClose}
                onCommentsChanged={() => refetch()}
              />
            ) : (
              <PublicThreadDetailView
                classPostUid={view.target.classPostUid}
                postTitle={view.postTitle}
                onBack={() => setView({ kind: VIEW.LIST })}
                onClose={handleClose}
                onCommentsChanged={() => refetch()}
              />
            )
          ) : view.kind === VIEW.NEW_THREAD ? (
            <NewThreadView
              {...(view.pendingPost.kind === "post"
                ? view.pendingPost.postTarget
                : {})}
              postTitle={
                view.pendingPost.kind === "post"
                  ? view.pendingPost.postTitle
                  : undefined
              }
              hideBackButton={false}
              onBack={() => setView({ kind: VIEW.LIST })}
              onClose={handleClose}
              onThreadCreated={handleThreadCreated}
            />
          ) : (
            <ThreadListView
              onConversationClick={handleConversationClick}
              onOpenGeneralThread={handleOpenGeneralThread}
              onClose={handleClose}
            />
          )}
        </MessageTeacherDrawerProvider>
      </Drawer>
    </>
  );
}
